import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { LayoutGrid, Users, Mail, Settings as SettingsIcon, LogOut, Shield, CalendarClock, Sun } from "lucide-react";
import Logo from "../../components/Logo";

/**
 * CRM auth-gated layout. Reuses the admin JWT (`cleany_admin_token`) —
 * anyone not logged in as admin is redirected to /admin/login.
 */
export default function CrmLayout() {
    const navigate = useNavigate();
    const [authed, setAuthed] = useState(null);

    useEffect(() => {
        const t = localStorage.getItem("cleany_admin_token");
        setAuthed(!!t);
        document.title = "Cleany · CRM";
    }, []);

    if (authed === null) return null;
    if (!authed) return <Navigate to="/admin/login?next=/crm" replace />;

    const handleLogout = () => {
        localStorage.removeItem("cleany_admin_token");
        navigate("/admin/login");
    };

    return (
        <div className="min-h-screen bg-slate-50" data-testid="crm-layout">
            <aside className="fixed inset-y-0 left-0 w-64 bg-slate-900 border-r border-slate-800 flex flex-col z-30">
                <div className="p-5 border-b border-slate-800">
                    <NavLink to="/crm" className="flex items-center gap-2 text-white font-display font-bold">
                        <Logo variant="light" />
                        <span className="text-xs font-semibold uppercase tracking-wider text-sky-400">CRM</span>
                    </NavLink>
                </div>
                <nav className="p-3 space-y-1 flex-1">
                    <NavItem to="/crm" icon={LayoutGrid} label="Pipeline" end testid="crm-nav-pipeline" />
                    <NavItem to="/crm/digest" icon={Sun} label="Dagens plan" testid="crm-nav-digest" />
                    <NavItem to="/crm/leads" icon={Users} label="Leads" testid="crm-nav-leads" />
                    <NavItem to="/crm/followups" icon={CalendarClock} label="Opfølgninger" testid="crm-nav-followups" />
                    <NavItem to="/crm/templates" icon={Mail} label="Mailskabeloner" testid="crm-nav-templates" />
                    <NavItem to="/crm/settings" icon={SettingsIcon} label="Indstillinger" testid="crm-nav-settings" />
                    <div className="pt-3 mt-3 border-t border-slate-800/70">
                        <NavLink
                            to="/admin"
                            data-testid="crm-nav-admin"
                            className="flex items-center justify-between gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                        >
                            <span className="flex items-center gap-3">
                                <Shield className="h-4 w-4" />
                                Admin
                            </span>
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Åbn</span>
                        </NavLink>
                    </div>
                </nav>
                <div className="p-4 border-t border-slate-800">
                    <button
                        type="button"
                        onClick={handleLogout}
                        data-testid="crm-logout-btn"
                        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm font-medium h-9"
                    >
                        <LogOut className="h-4 w-4" />
                        Log ud
                    </button>
                </div>
            </aside>
            <main className="ml-64 min-h-screen">
                <Outlet />
            </main>
        </div>
    );
}

function NavItem({ to, icon: Icon, label, end, testid }) {
    return (
        <NavLink
            to={to}
            end={end}
            data-testid={testid}
            className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                        ? "bg-sky-600 text-white"
                        : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`
            }
        >
            <Icon className="h-4 w-4" />
            {label}
        </NavLink>
    );
}
